/* SoundtrackPicker — pill button in the corner of the Landing page that
   opens a calm menu of curated soundtracks. Users can:
     • Pick any track from the MyLeaf library (8 curated pieces)
     • Upload their own audio file
         - Logged out → plays locally (browser-only) with a hint to sign in
         - Logged in  → uploads to their account, follows them across
                         devices/browsers, appears as "Your saved track"
     • Pause the current track
   No autoplay. Respects the existing `AmbientMusicButton` singleton so
   playback survives route changes. */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Music, Pause, Upload, Play, Check, ChevronDown, Save, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, formatApiError, BACKEND_URL, API } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// --- singleton audio shared with AmbientMusicButton ------------------------
let __ambient_audio = null;
const __ambient_subscribers = new Set();
let __ambient_error_toast_for = null;

function notify(state) {
  for (const fn of __ambient_subscribers) {
    try { fn(state); } catch { /* noop */ }
  }
}

function ensureAudio() {
  if (!__ambient_audio) {
    __ambient_audio = new Audio();
    __ambient_audio.loop = true;
    // 0.55 — comfortably audible on phone speakers. Previous values (0.16
    // then 0.45) drew the "shows playing but no sound" complaint.
    __ambient_audio.volume = 0.55;
    __ambient_audio.preload = 'auto';
    // NOTE: do NOT set crossOrigin = 'use-credentials'. The audio runs
    // same-origin in the preview environment and Bearer/cookie auth
    // flows through normally without the credential flag — and on the
    // production custom domain it would force CORS validation against
    // an `ACAO: *` reply and silently fail.
    __ambient_audio.addEventListener('playing', () => notify('playing'));
    __ambient_audio.addEventListener('pause',   () => notify('paused'));
    __ambient_audio.addEventListener('ended',   () => notify('paused'));
    __ambient_audio.addEventListener('emptied', () => notify('paused'));
    __ambient_audio.addEventListener('waiting', () => notify('loading'));
    __ambient_audio.addEventListener('stalled', () => notify('loading'));
    __ambient_audio.addEventListener('error', () => {
      notify('error');
      const failedSrc = __ambient_audio?.currentSrc || __ambient_audio?.src;
      if (failedSrc && __ambient_error_toast_for !== failedSrc) {
        __ambient_error_toast_for = failedSrc;
        toast.error('This soundtrack could not be played. Try another piece.');
      }
    });
  }
  return __ambient_audio;
}

export function SoundtrackPicker({ initialTrackId = 'nuvole' }) {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('paused');
  const [currentTrackId, setCurrentTrackId] = useState(null);
  const [currentLabel, setCurrentLabel] = useState('');
  // Pending local upload (not yet pushed to the account). Also used as the
  // playback source while logged-out users preview their pick locally.
  const [pendingUpload, setPendingUpload] = useState(null);  // {file, blobUrl, name}
  const [savedMusic, setSavedMusic] = useState(null);  // backend record once saved
  const [savingToAccount, setSavingToAccount] = useState(false);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);
  const subRef = useRef(null);

  // --- load curated library + (if logged in) the user's saved track -------
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/music/tracks`)
      .then(({ data }) => { if (!cancelled) setTracks(data.tracks || []); })
      .catch(() => { if (!cancelled) setTracks([]); });
    return () => { cancelled = true; };
  }, []);

  const loadSavedMusic = useCallback(async () => {
    if (!user) { setSavedMusic(null); return; }
    try {
      const { data } = await api.get('/users/me/music');
      setSavedMusic(data?.custom_music || null);
    } catch { setSavedMusic(null); }
  }, [user]);

  useEffect(() => { loadSavedMusic(); }, [loadSavedMusic]);

  // Singleton state subscription.
  useEffect(() => {
    subRef.current = (s) => setState(s);
    __ambient_subscribers.add(subRef.current);
    if (__ambient_audio && !__ambient_audio.paused && __ambient_audio.currentTime > 0) {
      setState('playing');
    }
    return () => __ambient_subscribers.delete(subRef.current);
  }, []);

  // Outside-click closes the menu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const playSource = (src, label, trackId) => {
    const a = ensureAudio();
    try {
      if (!a.paused) a.pause();
      if (src && a.src !== src) {
        a.src = src;
        __ambient_error_toast_for = null;
        // Safari requires an explicit load() after changing src for the
        // new file to actually start fetching / decoding.
        try { a.load(); } catch (_e) { /* noop */ }
      }
      if (src) {
        const p = a.play();
        if (p && typeof p.then === 'function') {
          p.catch(() => {
            // Autoplay-policy / gesture / codec — surface a calm hint so
            // the picker UI never silently lies that music is playing.
            toast('Tap the music button to start playback.', { duration: 2500 });
          });
        }
        setCurrentTrackId(trackId);
        setCurrentLabel(label);
      }
    } catch { /* noop */ }
  };

  const pickLibraryTrack = (t) => {
    if (t.kind === 'silence') {
      const a = ensureAudio();
      if (!a.paused) a.pause();
      setCurrentTrackId('silence');
      setCurrentLabel('Silence');
      setOpen(false);
      return;
    }
    const src = `${BACKEND_URL}${t.preview_url}`;
    playSource(src, t.name, t.track_id);
    setOpen(false);
  };

  const playSavedTrack = () => {
    const src = `${API}/users/me/music/file.mp3?v=${encodeURIComponent(savedMusic.uploaded_at || '')}`;
    playSource(src, savedMusic.filename || 'Your saved track', '__saved__');
    setOpen(false);
  };

  const togglePlayback = () => {
    const a = ensureAudio();
    if (!a.src) { setOpen(true); return; }
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          toast('Tap a track in the menu to start playback.', { duration: 2500 });
        });
      }
    } else {
      a.pause();
    }
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      toast.error('Please pick an audio file (mp3, m4a, wav, aac).');
      return;
    }
    if (file.size > 75 * 1024 * 1024) {
      toast.error('File is over 75 MB. Pick a smaller audio file.');
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    const niceName = (file.name || 'Your track').replace(/\.[^.]+$/, '');
    setPendingUpload({ file, blobUrl, name: niceName });
    // Start previewing immediately so the user hears it.
    playSource(blobUrl, niceName, '__pending__');
  };

  const saveToAccount = async () => {
    if (!user || !pendingUpload) return;
    setSavingToAccount(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingUpload.file);
      const { data } = await api.post('/users/me/music/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const cm = data?.custom_music || null;
      setSavedMusic(cm);
      // Switch playback to the persisted, account-bound source so it is
      // identical to what the user will hear after login on a new device.
      if (cm) {
        const src = `${API}/users/me/music/file.mp3?v=${encodeURIComponent(cm.uploaded_at || '')}`;
        playSource(src, cm.filename || pendingUpload.name, '__saved__');
      }
      // Drop the local blob — the cloud copy is the source of truth now.
      try { URL.revokeObjectURL(pendingUpload.blobUrl); } catch { /* noop */ }
      setPendingUpload(null);
      toast.success('Saved to your account — your music will follow you to any device.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingToAccount(false);
    }
  };

  const removeSaved = async () => {
    if (!user || !savedMusic) return;
    if (!window.confirm('Remove your saved music? You can upload another anytime.')) return;
    try {
      await api.delete('/users/me/music');
      const a = ensureAudio();
      if (currentTrackId === '__saved__') {
        if (!a.paused) a.pause();
        setCurrentTrackId(null);
        setCurrentLabel('');
        a.removeAttribute('src');
        a.load?.();
      }
      setSavedMusic(null);
      toast.success('Saved music removed');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const playing = state === 'playing';
  const loading = state === 'loading';
  const displayLabel = currentLabel || 'Music';

  return (
    <div
      ref={menuRef}
      className="fixed top-[88px] right-4 sm:right-6 z-30"
      data-testid="soundtrack-picker"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlayback}
          aria-label={playing ? `Pause ${displayLabel}` : `Play ${displayLabel}`}
          title={playing ? `Pause ${displayLabel}` : `Play ${displayLabel}`}
          data-testid="soundtrack-toggle"
          className={
            'flex items-center gap-2 px-3 py-2 rounded-full border ' +
            'bg-[hsl(var(--surface))]/90 backdrop-blur-sm shadow-sm ' +
            'transition-all duration-300 ' +
            (playing
              ? 'border-[hsl(var(--accent))] text-[hsl(var(--accent))]'
              : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary)/0.4)]')
          }
        >
          {playing ? <Pause size={13} strokeWidth={2} /> : <Music size={13} strokeWidth={1.5} />}
          <span className="text-[10px] tracking-[0.22em] uppercase font-medium">
            {playing
              ? `${displayLabel} · On`
              : (loading ? 'Loading…' : displayLabel)}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Change soundtrack"
          data-testid="soundtrack-open"
          className="flex items-center gap-1 px-2.5 py-2 rounded-full border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--surface))]/90 backdrop-blur-sm shadow-sm text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary)/0.4)] transition-all duration-300"
        >
          <ChevronDown
            size={13}
            strokeWidth={1.5}
            className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {open && (
        <div
          role="menu"
          data-testid="soundtrack-menu"
          className="absolute right-0 top-full mt-3 w-[22rem] max-w-[calc(100vw-2rem)] rounded-md border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--surface))] shadow-[0_30px_60px_-30px_rgba(26,54,38,0.45)] overflow-hidden"
        >
          <div className="px-5 pt-4 pb-3 border-b border-[hsl(var(--primary)/0.08)]">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--accent))] font-semibold">
              Soundtrack
            </div>
            <div className="font-serif text-[18px] text-[hsl(var(--primary))] mt-1">
              Choose a piece of music
            </div>
          </div>

          <ul className="py-2 max-h-96 overflow-y-auto">
            {/* User's saved track (if signed in + has one) */}
            {user && savedMusic && (
              <li>
                <button
                  type="button"
                  onClick={playSavedTrack}
                  data-testid="soundtrack-saved-track"
                  className="w-full flex items-start gap-3 px-5 py-3 text-left hover:bg-[hsl(var(--accent)/0.08)] transition-colors border-l-2 border-[hsl(var(--accent))]"
                >
                  <div className="mt-1 w-5 shrink-0 flex justify-center">
                    {currentTrackId === '__saved__' ? (
                      <Check size={14} className="text-[hsl(var(--accent))]" strokeWidth={2.5} />
                    ) : (
                      <Play size={12} className="text-[hsl(var(--accent))]" strokeWidth={2} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-serif text-[16px] text-[hsl(var(--primary))] truncate">
                        {savedMusic.filename || 'Your saved track'}
                      </div>
                      <span className="text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--accent))] text-white">
                        Saved
                      </span>
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--accent))] mt-0.5 font-semibold">
                      Your account · plays on every device
                    </div>
                  </div>
                </button>
                <div className="px-5 pb-2 -mt-1">
                  <button
                    type="button"
                    onClick={removeSaved}
                    className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--destructive))] inline-flex items-center gap-1.5 transition-colors"
                    data-testid="soundtrack-saved-remove"
                  >
                    <Trash2 size={11} strokeWidth={2} /> Remove saved music
                  </button>
                </div>
              </li>
            )}

            {tracks.map((t) => {
              const isCurrent = currentTrackId === t.track_id;
              return (
                <li key={t.track_id}>
                  <button
                    type="button"
                    onClick={() => pickLibraryTrack(t)}
                    data-testid={`soundtrack-track-${t.track_id}`}
                    className="w-full flex items-start gap-3 px-5 py-3 text-left hover:bg-[hsl(var(--primary)/0.04)] transition-colors"
                  >
                    <div className="mt-1 w-5 shrink-0 flex justify-center">
                      {isCurrent ? (
                        <Check size={14} className="text-[hsl(var(--accent))]" strokeWidth={2} />
                      ) : (
                        <Play size={12} className="text-[hsl(var(--primary)/0.4)]" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-[16px] text-[hsl(var(--primary))]">{t.name}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.5)] mt-0.5">
                        {t.mood}
                      </div>
                      {t.description && (
                        <div className="text-xs text-[hsl(var(--primary)/0.6)] font-light leading-relaxed mt-1">
                          {t.description}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Upload + save panel */}
          <div className="border-t border-[hsl(var(--primary)/0.08)] px-5 py-4 space-y-3">
            {/* Pending local upload — surfaces the explicit "Save to my account" CTA */}
            {pendingUpload && (
              <div
                className="rounded-md border border-[hsl(var(--accent))]/45 bg-[hsl(var(--accent)/0.08)] p-3"
                data-testid="soundtrack-pending-upload"
              >
                <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--primary))] truncate">
                  {pendingUpload.name}
                </div>
                <div className="mt-0.5 text-[11px] text-[hsl(var(--primary)/0.7)] font-medium">
                  Previewing locally in this browser
                </div>
                {user ? (
                  <button
                    type="button"
                    onClick={saveToAccount}
                    disabled={savingToAccount}
                    data-testid="soundtrack-save-to-account-btn"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[hsl(var(--accent))] text-white px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-[hsl(var(--accent)/0.88)] transition-colors disabled:opacity-60"
                  >
                    <Save size={13} strokeWidth={2} />
                    {savingToAccount ? 'Saving…' : 'Save to my account'}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-sm border border-[hsl(var(--primary))] bg-transparent text-[hsl(var(--primary))] px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] transition-colors"
                    data-testid="soundtrack-login-to-save"
                  >
                    Log in to save uploaded music across devices
                  </Link>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="soundtrack-upload-btn"
              className="w-full flex items-center justify-center gap-2 rounded-sm border border-[hsl(var(--primary)/0.25)] bg-transparent px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.85)] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary)/0.6)] transition-colors"
            >
              <Upload size={13} strokeWidth={1.8} />
              {pendingUpload ? 'Choose a different file' : 'Upload your own'}
            </button>

            <div className="text-[10px] text-[hsl(var(--primary)/0.6)] text-center font-medium">
              {user
                ? 'mp3 · m4a · wav · max 75 MB. Save to your account so it plays on every device.'
                : 'Log in to save uploaded music across devices.'}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.m4a,.wav,.aac"
              className="hidden"
              onChange={onUpload}
              data-testid="soundtrack-upload-input"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SoundtrackPicker;
